import type Blocklist from '../blocklist/Blocklist'
import Logger from '../helpers/Logger'
import type WebModel from '../models/WebModel'
import type { Slot } from './Slot'
import { SlotRenderer } from './SlotRenderer'
import SlotRepository from './SlotRepository'
import SlotCounter from './SlotCounter'
import type { AuctionHouse } from '../auctionHouse/AuctionHouse'
import EventManager from '../eventManager/EventManager'

export default class SlotFactory {
  auctionHouse: AuctionHouse
  constructor(private model: WebModel, private blocklist: Blocklist) {}

  /**
   * Sets up the slotRenderEnded handler, given the passed in AuctionHouse
   *
   * Note: We used to pass in an AuctionHouse in the constructor.  This function exists
   * solely to help decrease the beforeScroll dependencies we have, in order to help our Lighthouse Score.
   * Using this, we can avoid loading AuctionHouse before scroll.
   * @param auctionHouse
   */
  registerSlotRenderEndedHandler(auctionHouse: AuctionHouse) {
    if (!this.auctionHouse) {
      this.auctionHouse = auctionHouse

      EventManager.on(EventManager.events.slotRenderEnded, (event, slot) => {
        if (slot) {
          SlotRenderer.handleRenderEnded(event, slot, auctionHouse)
        }
      })
    }
  }

  async new(slotModel: ISlotModel): Promise<Slot | undefined> {
    try {
      if (this.blocklist.all || this.blocklist.contains(slotModel.adUnitId)) {
        return undefined
      }

      const SlotConstructor = await this.getSlotConstructor(slotModel)
      if (!SlotConstructor) {
        throw `Couldn't find SlotConstructor for ${slotModel.type}`
      }

      const slot: Slot = new SlotConstructor(slotModel, this.model)

      // Now that we have the final baseAdunitId and selector, check the blocklist
      const blocklisted = this.blocklist.isBlockListed(slot)
      if (blocklisted) {
        return undefined
      }

      if (SlotRenderer.render(slot)) {
        SlotCounter.increment(slotModel.type)
        Logger.debug(`Rendered slot ${slot.id}`)
        SlotRepository.add(slot)

        return slot
      }

      return undefined
    } catch (e) {
      Logger.debug(e)
      Logger.debug(`Slot ${slotModel.type} unable to instantiate!`)
    }
  }

  // This allows us to Dynamically load only the slots that appear on the page.
  async getSlotConstructor(
    slotModel: ISlotModel
  ): Promise<Constructor<Slot> | null> {
    switch (slotModel.type) {
      case 'Adhesion':
        const { Adhesion } = await import('./Adhesion')
        return Adhesion
      case 'Arrival':
        const { Arrival } = await import('./Arrival')
        return Arrival
      case 'Comments':
        const { Comments } = await import('./Comments')
        return Comments
      case 'Content':
        const { Content } = await import('./Content')
        return Content
      case 'Feed':
        const { Feed } = await import('./Feed')
        return Feed
      // case 'Interstitial':
      //   const {Interstitial} = await import('./Interstitial')
      //   return Interstitial
      case 'Leaderboard':
        const { Leaderboard } = await import('./Leaderboard')
        return Leaderboard
      case 'LeaderboardAtf':
        const { LeaderboardAtf } = await import('./LeaderboardAtf')
        return LeaderboardAtf
      case 'LeaderboardBtf':
        const { LeaderboardBtf } = await import('./LeaderboardBtf')
        return LeaderboardBtf
      case 'Recipe':
        const { Recipe } = await import('./Recipe')
        return Recipe
      case 'Sidebar':
        const { Sidebar } = await import('./Sidebar')
        return Sidebar
      case 'SidebarAtf':
        const { SidebarAtf } = await import('./SidebarAtf')
        return SidebarAtf
      case 'SidebarBtf':
        const { SidebarBtf } = await import('./SidebarBtf')
        return SidebarBtf
      case 'UniversalPlayer':
        const { UniversalPlayer } = await import('./UniversalPlayer')
        return UniversalPlayer
      default:
        return null
    }
  }
}
